import type { ProposalOrder } from '@moment/horizon-data-access/proposal'
import type {
  LiveInventoryItem,
  LiveInventoryPostResponse,
} from '@/types/api/live-inventory'
import type { MarksApiResponse } from '@/types/api/prices'
import type { OrderSide } from '@/types/enums/order'

import Input from '@/components/atomic/input'
import { BodyMediumSubtext, FontWeight } from '@/components/atomic/text'
import { OrderSide as OrderSideComponent } from '@/components/molecules/badges/order-side'
import { getRatingDisplay } from '@/hooks/use-rating-columns'
import { formatQuantity, formatYield } from '@/utils/presentation/bonds'
import {
  formatMoney,
  formatNumber,
  formatPercentage,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'

import { OrderDetailsContainer } from './styles'

interface SummaryItem {
  label: string
  value: string | null
}

type LiveInventoryReturnValues = keyof LiveInventoryItem

export function getSummaryItems(
  referenceData: LiveInventoryPostResponse | undefined,
  priceData: MarksApiResponse[] | undefined,
  ratingsAccess?: {
    hasSPAccess: boolean | null
    hasMoodysAccess: boolean | null
  },
): SummaryItem[] {
  const items = [
    {
      label: 'Issuer',
      value: referenceData?.data[0]?.issuer ?? PLACEHOLDER,
    },
    {
      label: 'Fair value',
      value: formatMoney(priceData?.[0]?.details?.price || 0) ?? PLACEHOLDER,
    },
    {
      label: 'YTW',
      value:
        formatPercentage(priceData?.[0]?.details?.yield_to_worst || 0, {
          numDigits: 3,
        }) ?? PLACEHOLDER,
    },
    {
      label: 'Duration',
      value:
        formatNumber(referenceData?.data[0]?.duration || 0, {
          maximumFractionDigits: 3,
        }) ?? PLACEHOLDER,
    },
  ]

  if (ratingsAccess) {
    const ratingDisplay = getRatingDisplay(
      { ...ratingsAccess, isLoading: false },
      referenceData?.data[0]?.moodys_rating,
      referenceData?.data[0]?.sp_rating,
    )

    items.push({ label: 'Rating', value: ratingDisplay.value })
  } else {
    items.push({
      label: 'Credit rating',
      value: referenceData?.data[0]?.sp_rating ?? PLACEHOLDER,
    })
  }

  return items
}

export function OrderDetails({
  order,
  inputPrice,
  setInputPrice,
  impliedYTW,
  handlePriceUpdate,
  isDisabled = false,
}: {
  order: ProposalOrder | undefined
  inputPrice: number | null
  setInputPrice: (price: number | null) => void
  impliedYTW: number | null
  handlePriceUpdate: () => void
  isDisabled?: boolean
}) {
  return (
    <OrderDetailsContainer>
      <div className="flex items-center space-x-1.5">
        <OrderSideComponent
          side={
            (order?.side as OrderSide.Buy) || (order?.side as OrderSide.Sell)
          }
        />
        <BodyMediumSubtext
          fontWeight={FontWeight.Medium}
          className="whitespace-nowrap"
        >
          {formatQuantity(order?.quantity ?? 0)} at
        </BodyMediumSubtext>

        <Input
          width="100px"
          placeholder="Price"
          value={inputPrice?.toString() ?? ''}
          type="number"
          size="small"
          step={1}
          disabled={isDisabled}
          slotProps={{
            side: 'left',
            label: '$',
            showSeparator: true,
          }}
          onChange={(e) =>
            setInputPrice(e.target.value ? Number(e.target.value) : null)
          }
          onBlur={handlePriceUpdate}
        />
        <BodyMediumSubtext
          fontWeight={FontWeight.Medium}
          className="whitespace-nowrap"
        >
          • {formatYield(impliedYTW ?? order?.limit_ytw)}
        </BodyMediumSubtext>
      </div>
    </OrderDetailsContainer>
  )
}

/**
 * Helper function to get inventory query parameters
 * @param isin - International Securities Identification Number
 * @param riskGroupId - Risk group identifier
 */
export function getInventoryQueryParams(isin: string, riskGroupId: string) {
  return {
    filters: {
      isin: { equal_to: isin },
    },
    returns: [
      'isin',
      'issuer',
      'duration',
      'sp_rating',
      'moodys_rating',
      'next_call_price',
      'close_price',
      'close_ytw',
      'next_call_date',
      'next_call_price',
      'type',
    ] as LiveInventoryReturnValues[],
    risk_group_id: riskGroupId,
  }
}
